if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    // The notification/drawer only counts once its element is upgraded. /cart
    // renders no header on R3 (layout/theme.liquid), so cart-notification.js
    // never loads there while a bare <cart-notification> can still be in the
    // DOM — calling getSectionsToRender() on it threw and left the shelf's
    // add button spinning forever (owner, 2026-09-29). Resolved per submit,
    // not in the constructor, because script order differs between pages.
    resolveCart() {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      return cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function'
        ? cart
        : null;
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      this.startAdd();
    }

    // The add itself. A tap has usually started it already, from the click
    // (see the listener at the end of this file): the submit that follows
    // finds the button busy and stops here, so nothing is added twice.
    startAdd() {
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.cart = this.resolveCart();
      // Prepare the confirmation dependency before writing the cart. A failed
      // header asset must not silently switch a product add into navigation.
      if (!this.cart && !this.getCartPageSectionsToRender().length) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');
        return this.loadCartNotification().then(() => {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          if (this.resolveCart()) this.startAdd();
          else this.showError(this.message('addFailed'));
        });
      }

      this.handleErrorMessage();
      const toast = document.getElementById('sondos-cart-toast');
      if (toast) toast.classList.remove('is-visible');
      clearTimeout(ProductForm.toastTimer);

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      // On /cart the shopper can add from "ربما يعجبك أيضاً". `this.cart` is the notification
      // (or drawer), so only that got re-rendered — the cart lines and totals on the page
      // behind it kept showing the pre-add state until a manual refresh. Ask for the cart
      // page's own sections in the same request and render them too.
      const cartPageSections = this.getCartPageSectionsToRender();
      if (this.cart || cartPageSections.length) {
        const sectionIds = (this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [])
          .concat(cartPageSections.map((section) => section.section));
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
        if (this.cart) this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      const variantId = Number(formData.get('id')) || null;
      // /cart: the add joins the page's write queue (assets/cart.js), so its
      // response can never repaint over a quantity change made after it, and
      // checkout waits for it (QA-05, R-02). The header's cart-notification is
      // live on /cart too; it does not change where the add is painted.
      const sync = cartPageSections.length ? window.SondosCartSync || null : null;
      const job = () => this.sendAdd(config, variantId, cartPageSections, sync);
      const done = sync ? sync.enqueue(job) : job();

      Promise.resolve(done)
        .catch((e) => console.error(e))
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    // One POST /cart/add.js. A response we cannot read (no network, an HTML
    // error page, a 5xx) does not say whether the item was added, so the cart
    // is read back before she is told anything or invited to tap again.
    sendAdd(config, variantId, cartPageSections, sync) {
      const before = sync ? this.variantQuantity(sync.confirmed(), variantId) : null;
      const intent = cartPageSections.length ? null : (ProductForm.notificationIntent = (ProductForm.notificationIntent || 0) + 1);
      return fetch(`${routes.cart_add_url}`, config)
        .then(
          (response) => response.text().then((text) => ({ text, status: response.status, ok: response.ok })),
          () => ({ text: null, status: 0, ok: false })
        )
        .then(({ text, status, ok }) => {
          let response = null;
          if (text != null) {
            try { response = JSON.parse(text); } catch (e) { response = null; }
          }
          if (!response || status >= 500 || (!ok && !response.status && !response.description)) {
            console.error('[product-form] /cart/add outcome unknown', { status });
            return this.resolveUnknownAdd(variantId, before, sync, intent);
          }

          if (!ok || response.status) {
            this.error = false;
            this.showError(response.description || response.message || this.message('addFailed'));

            // A temporary refusal (for example 429) is not a stock result.
            // Leave the button retryable instead of stranding it as sold out.
            // Stock refusals keep the existing 422 behavior below.
            if (status !== 422) return null;

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return null;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return null;
          }

          this.error = false;
          if (!this.notificationIsCurrent(intent)) {
            // Intent order is not server commit order. Keep the newer product
            // visible, but reconcile totals after this older write completes.
            this.markNotificationCountDirty();
            return null;
          }
          if (intent != null) ProductForm.confirmedIntent = intent;
          if (!this.cart) {
            // The UI may have changed since the add started. Update the cart
            // page in place, or recover its confirmation without navigation.
            if (cartPageSections.length) {
              this.error = false;
              return this.finishCartPageAdd(response, sync);
            }
            return this.renderCartNotification(response, intent);
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            this.renderCartPageSections(response);
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.renderCartNotification(response, intent) });
            }, { once: true });
            quickAddModal.hide(true);
          } else if (cartPageSections.length) {
            // /cart only. Every other template still opens the notification.
            return this.finishCartPageAdd(response, sync);
          } else {
            return this.renderCartNotification(response, intent);
          }
          return null;
        });
    }

    loadCartNotification() {
      if (this.resolveCart()) return Promise.resolve();
      const asset = document.querySelector('script[src*="/cart-notification.js"]');
      if (!document.querySelector('cart-notification') || !asset) return Promise.resolve();
      if (!ProductForm.notificationLoad) {
        ProductForm.notificationLoad = new Promise((resolve) => {
          let script;
          let finished = false;
          const done = () => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            asset.removeEventListener('load', loaded);
            asset.removeEventListener('error', retry);
            document.removeEventListener('DOMContentLoaded', retry);
            if (script) script.onload = script.onerror = null;
            resolve();
          };
          const retry = () => {
            if (finished) return;
            if (this.resolveCart()) { done(); return; }
            if (script) return;
            script = document.createElement('script');
            script.src = asset.src;
            script.async = true;
            script.onload = script.onerror = done;
            document.head.appendChild(script);
          };
          const loaded = () => { if (this.resolveCart()) done(); else retry(); };
          const timer = setTimeout(done, 8000);
          // The owned defer asset may still be loading. Its registration ends
          // the wait even if a fallback copy has not completed; never hold a
          // usable component behind that redundant download.
          customElements.whenDefined('cart-notification').then(() => { if (!finished && this.resolveCart()) done(); });
          const nav = performance.getEntriesByType('navigation')[0];
          if (asset.defer && !asset.async && document.readyState !== 'complete' && !(nav && nav.domContentLoadedEventStart > 0)) {
            asset.addEventListener('load', loaded);
            asset.addEventListener('error', retry);
            document.addEventListener('DOMContentLoaded', retry, { once: true });
          } else retry();
        }).finally(() => { ProductForm.notificationLoad = null; });
      }
      return ProductForm.notificationLoad;
    }

    notificationIsCurrent(intent) {
      return intent == null || intent >= (ProductForm.confirmedIntent || 0);
    }

    markNotificationCountDirty() {
      ProductForm.countRevision = (ProductForm.countRevision || 0) + 1;
      const notification = this.resolveCart();
      if (notification && ProductForm.renderedIntent === ProductForm.confirmedIntent) {
        this.refreshNotificationCount(notification, ProductForm.confirmedIntent);
      }
    }

    // Bound read-only display recovery, including response-body parsing. An
    // add POST is never cancelled/repeated by this deadline. Late read results
    // cannot resolve the already rejected promise and repaint stale state.
    readCartDisplay(url) {
      const controller = window.AbortController ? new AbortController() : null;
      let timer;
      return new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          if (controller) controller.abort();
          reject(new Error('Cart display read timed out'));
        }, 4000);
        fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller ? controller.signal : undefined })
          .then((result) => {
            if (!result.ok) throw new Error(`Cart display read: ${result.status}`);
            return result.json();
          }).then(resolve, reject);
      }).finally(() => clearTimeout(timer));
    }

    refreshNotificationCount(notification, intent) {
      const version = notification.displayVersion;
      const revision = ProductForm.countRevision || 0;
      const pending = ProductForm.countRefresh;
      if (pending && pending.notification === notification && pending.version === version && pending.intent === intent && pending.revision === revision) return;
      const job = { notification, version, intent, revision };
      ProductForm.countRefresh = job;
      this.readCartDisplay(`${window.routes.cart_url}.js`).then((cart) => {
        const current = this.resolveCart();
        if (!this.notificationIsCurrent(intent) || current !== notification || notification.displayVersion !== version || revision !== (ProductForm.countRevision || 0)) {
          // A newer display may have used a snapshot from before the late
          // write. Reconcile that current display, never reuse this old read.
          if (ProductForm.countRefresh === job && current && (ProductForm.countRevision || 0) > (ProductForm.syncedCountRevision || 0)) {
            this.refreshNotificationCount(current, ProductForm.confirmedIntent);
          }
          return;
        }
        if (!cart || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) return;
        this.syncCartChrome(cart);
        if (typeof notification.updateConfirmedCount === 'function') notification.updateConfirmedCount(cart);
        if (ProductForm.renderedIntent === intent) ProductForm.syncedCountRevision = revision;
      }).catch(() => {}).finally(() => {
        if (ProductForm.countRefresh === job) ProductForm.countRefresh = null;
      });
    }

    // The write is confirmed. Prefer the existing confirmed-line renderer if
    // bundled HTML failed; a second network response must not gate success.
    renderCartNotification(response, intent) {
      if (!this.notificationIsCurrent(intent)) return null;
      this.cart = this.resolveCart();
      if (!this.cart) {
        return this.loadCartNotification().then(() => this.renderConfirmedNotification(response, intent));
      }
      try {
        this.cart.renderContents(response);
        if (intent != null) ProductForm.renderedIntent = intent;
        if ((ProductForm.countRevision || 0) > (ProductForm.syncedCountRevision || 0)) this.refreshNotificationCount(this.cart, intent);
        return null;
      } catch (error) {
        if (typeof this.cart.renderConfirmedItem === 'function') return this.renderConfirmedNotification(response, intent);
        // Preserve the drawer's HTML-only contract; R3's notification takes
        // the immediate JSON path above. Guard and bound this fallback too.
        const url = new URL(window.location.href);
        url.searchParams.set('sections', this.cart.getSectionsToRender().map((section) => section.id).join(','));
        const cart = this.cart;
        return this.readCartDisplay(url.pathname + url.search)
          .then((sections) => {
            if (!this.notificationIsCurrent(intent) || this.resolveCart() !== cart) return;
            this.cart = this.resolveCart();
            if (!this.cart) throw new Error('Cart display unavailable');
            this.cart.renderContents(Object.assign({}, response, { sections }));
          })
          .catch((refreshError) => {
            return this.renderConfirmedNotification(response, intent);
          });
      }
    }

    renderConfirmedNotification(response, intent) {
      if (!this.notificationIsCurrent(intent)) return null;
      this.cart = this.resolveCart();
      if (!this.cart || typeof this.cart.renderConfirmedItem !== 'function') {
        this.showError((window.cartStrings && window.cartStrings.itemAdded) || this.message('addUnsure'));
        return null;
      }
      const notification = this.cart;
      notification.renderConfirmedItem(response, null);
      if (intent != null) ProductForm.renderedIntent = intent;
      // The button is released after showing the confirmed item. Only the
      // known count is refreshed later; never rerender/reopen the dialog or
      // let an old read overwrite a newer add or replacement component.
      this.markNotificationCountDirty();
      return null;
    }

    variantQuantity(lines, variantId) {
      return (lines || []).reduce((sum, line) => sum + (String(line.variant) === String(variantId) ? line.quantity : 0), 0);
    }

    resolveUnknownAdd(variantId, before, sync, intent) {
      if (sync) {
        // /cart: the queue knows what was confirmed before this add.
        sync.markUnknown();
        return sync.ensureReconciledNow().then(() => {
          const after = this.variantQuantity(sync.confirmed(), variantId);
          if (after > before) {
            this.error = false;
            this.showCartPageAddSuccess();
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'product-form' } }));
            return null;
          }
          this.showError(this.message('addFailed'));
          return null;
        }, () => this.showError(this.message('addUnsure')));
      }
      // Elsewhere: if the product is not in the cart at all the add surely
      // failed and a retry is safe; if it is, she is sent to check first.
      return this.readCartDisplay(`${window.routes.cart_url}.js`)
        .then((cart) => {
          if (!cart || !Number.isSafeInteger(cart.item_count) || !Array.isArray(cart.items)) throw new Error('Cart state unavailable');
          if (this.notificationIsCurrent(intent)) this.syncCartChrome(cart);
          else this.markNotificationCountDirty();
          const present = Array.isArray(cart.items) && cart.items.some((item) => String(item.variant_id) === String(variantId));
          this.showError(this.message(present ? 'addUnsure' : 'addFailed'));
        })
        .catch(() => {
          if (!this.notificationIsCurrent(intent)) this.markNotificationCountDirty();
          this.showError(this.message('addUnsure'));
        });
    }

    message(key) {
      const strings = window.cartStrings || {};
      const fallback = {
        addFailed: 'ما قدرنا نضيف المنتج. حاولي مرة ثانية.',
        addUnsure: 'ما تأكدنا إذا انضاف المنتج بسبب الشبكة. افتحي السلة وتأكدي قبل ما تعيدي المحاولة.'
      };
      return strings[key] || fallback[key] || strings.error || '';
    }

    // Visible, in Arabic, wherever this form lives (QA-09): the form's own
    // error line when it has one (PDP), else the sheet it sits in, else the
    // shared toast (product cards have no error line).
    showError(message) {
      if (!message) return;
      this.handleErrorMessage(message);
      if (this.errorMessageWrapper) return;
      const unhandled = this.dispatchEvent(new CustomEvent('product-form:error', {
        bubbles: true,
        cancelable: true,
        detail: { message }
      }));
      if (!unhandled) return;
      let toast = document.getElementById('sondos-cart-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'sondos-cart-toast';
        toast.className = 'sondos-cart-toast';
        toast.setAttribute('role', 'alert');
        toast.innerHTML = '<p class="sondos-cart-toast__text"></p>';
        document.body.appendChild(toast);
      }
      const text = toast.querySelector('.sondos-cart-toast__text');
      if (text) text.textContent = message;
      toast.classList.add('is-visible');
      clearTimeout(ProductForm.toastTimer);
      ProductForm.toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4200);
    }

    // The /cart page's own sections. Empty everywhere else, which is what makes the two
    // call sites above no-ops outside /cart.
    getCartPageSectionsToRender() {
      return [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.js-contents' }
      ]
        .map((entry) => {
          const el = document.getElementById(entry.id);
          return el && el.dataset.id ? Object.assign({}, entry, { section: el.dataset.id }) : null;
        })
        .filter(Boolean);
    }

    renderCartPageSections(response) {
      const sections = this.getCartPageSectionsToRender();
      if (!sections.length || !response || !response.sections) return;

      sections.forEach((entry) => {
        const html = response.sections[entry.section];
        if (!html) return;
        const container = document.getElementById(entry.id);
        if (!container) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const target = container.querySelector(entry.selector);
        const incoming = parsed.querySelector(entry.selector);

        if (target && incoming) {
          target.innerHTML = incoming.innerHTML;
          return;
        }
        // Selector missing on one side — swap the whole section body instead of grafting a
        // fragment into the wrong parent, which would drop the surrounding markup.
        const incomingContainer = parsed.getElementById(entry.id);
        if (incomingContainer) container.innerHTML = incomingContainer.innerHTML;
      });

      // An add always leaves the cart non-empty, so drop the empty-state class the page was
      // rendered with — otherwise the first add to an empty cart shows the new lines
      // underneath "your cart is empty".
      const cartItems = document.querySelector('cart-items');
      if (cartItems) cartItems.classList.remove('is-empty');
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.remove('is-empty');
      const checkout = document.getElementById('checkout');
      if (checkout) checkout.disabled = false;

      document.querySelectorAll('quantity-input').forEach((node) => {
        if (typeof node.syncButtonState === 'function') node.syncButtonState();
      });

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'product-form' }
      }));
    }

    // /cart only — never called from PDP / collection / home. Keeps her on the
    // "ربما يعجبك أيضاً" card: lines grow above, we shift scroll by that
    // growth so the button stays under her thumb, then the button itself
    // says the add landed.
    finishCartPageAdd(response, sync) {
      const items = document.getElementById('main-cart-items');
      const before = items ? items.getBoundingClientRect().height : 0;
      const after = () => {
        const height = items ? items.getBoundingClientRect().height : 0;
        const delta = height - before;
        if (delta) window.scrollBy(0, delta);
        this.showCartPageAddSuccess();
      };

      if (!sync) {
        this.renderCartPageSections(response);
        this.syncCartChrome(response);
        after();
        return null;
      }

      // /cart/add.js answers with the line, not the cart: read the cart (for
      // keys and count), then paint the add's own sections if they are whole,
      // else fetch them again — never a half-updated page (QA-04/05).
      return sync.getJson(sync.cartJsUrl())
        .then((cart) => {
          const specs = sync.sectionSpecs();
          const parsed = sync.parseSections(response && response.sections, specs);
          if (!parsed) return sync.reconcileNow('product-form').then(() => cart);
          sync.setConfirmed(cart);
          sync.paint(parsed, cart, 'product-form');
          return cart;
        })
        .catch(() => sync.reconcileNow('product-form'))
        .then((cart) => {
          this.syncCartChrome(cart);
          after();
        }, () => {
          // Added (the server said so) but the page could not catch up.
          sync.markUnknown();
          this.showCartPageAddSuccess();
          sync.setError('cart', sync.strings().refresh);
        });
    }

    showCartPageAddSuccess() {
      const btn = this.submitButton;
      if (!btn) return;
      btn.classList.add('is-added');
      const prevLabel = btn.getAttribute('aria-label');
      const msg = (window.cartStrings && window.cartStrings.itemAdded) || '';
      if (msg) btn.setAttribute('aria-label', msg);
      clearTimeout(this._cartAddedTimer);
      this._cartAddedTimer = setTimeout(function () {
        btn.classList.remove('is-added');
        if (prevLabel) btn.setAttribute('aria-label', prevLabel);
      }, 1600);
    }

    // Header badge + "3 قطع" live outside the section fragments we swap.
    // /cart/add.js answers with the added line, not the cart, so there is no
    // item_count on it: both stayed on the old number after every add on /cart
    // (found 2026-10-09 from the «أضيفي المزيد» sheet). Ask /cart.js for it.
    syncCartChrome(response) {
      const count = response && response.item_count;
      if (count == null) {
        fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((res) => res.json())
          .then((cart) => { if (cart && cart.item_count != null) this.syncCartChrome(cart); })
          .catch(() => {});
        return;
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.updateCartPageCount === 'function') {
        cartItems.updateCartPageCount(count);
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      const n = Math.max(0, Number(count) || 0);
      let badge = bubble.querySelector('.skh__cart-count');
      if (n === 0) {
        if (badge) badge.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'skh__cart-count';
        bubble.appendChild(badge);
      }
      badge.textContent = String(n);
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });

  // Tap → request before everyone else's click listeners. The add used to
  // leave only after the click had run every listener on the page — the Meta
  // and Google pixels and Shopify's pixel manager run theirs here, 85–550 ms
  // on a mid-range phone (4x CPU, measured on Live 2026-10-11). A capture
  // listener on window runs before all of them. They still get the same
  // click and the same submit; only the request leaves earlier.
  window.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button > 0) return;
    const button = event.target && event.target.closest ? event.target.closest('button') : null;
    if (!button || button.type !== 'submit' || button.disabled) return;
    const form = button.form;
    const productForm = form && form.closest('product-form');
    if (!productForm || productForm.form !== form || productForm.submitButton !== button) return;
    if (typeof productForm.startAdd !== 'function') return;
    // Anything the browser would refuse to submit takes the normal path.
    if (!form.noValidate && !form.checkValidity()) return;
    productForm.startAdd();
  }, true);
}
